import {
    PlatformMessage,
    Feature as PmcFeature,
} from '@reachmedianetwork/post-message-client-rds-api';
import { Permission } from '../Permissions';
import { Feature, isUrlQueryPlatform, hasUrlQueryFeature } from '../PlatformCheck';
import { PlayerPmc } from '../PlayerPmc';
import type {
    BrightSignInformationInterface,
    RequiredInterfaces,
    ScreenOrientationInterface,
    SignageReadyInterface,
    SystemInformationInterface,
    VideoOutputInterface,
} from '../interfaces';

export class BrightSignPmcPlayer
    extends PlayerPmc
    implements
        RequiredInterfaces,
        SystemInformationInterface,
        VideoOutputInterface,
        BrightSignInformationInterface,
        ScreenOrientationInterface,
        SignageReadyInterface
{
    /**
     * @todo Figure out a way to send "BrightSign-PMC" as the platform name. Currently this needs to
     * be "BrightSign" so that the CMS `pacman` code correctly sees the player as running
     * "BrightSign".
     *
     * I believe `pacman` uses the "getSystemConfig" message to get the platform type, if that is
     * the case we could update either `SystemData.platform` or `SystemData.player.platform`
     * depending on which one `pacman` looks at.
     */
    readonly name = 'BrightSign';

    constructor() {
        super();

        this.addPermission(Permission.NATIVE_HLS);
        this.addPermission(Permission.NATIVE_RTP);
        this.addPermission(Permission.RELAXED_NET_SECURITY);
    }

    static isTheRunningPlatform(): boolean {
        const thisPlatformIsRunning = isUrlQueryPlatform('BrightSign');
        const thisPlatformSupportsPmc = hasUrlQueryFeature(Feature.PostMessageClient);
        return thisPlatformIsRunning === true && thisPlatformSupportsPmc;
    }

    async initializePlatform(): Promise<void> {
        await super.initializePlatform();

        const feats = await this.postMessage(PlatformMessage.FeaturesGet, undefined);
        if (feats.includes(PmcFeature.MseSupport)) {
            this.removePermission(Permission.NATIVE_HLS);
        }
    }

    async _generateAndGetHardwareId(): Promise<string> {
        const deviceInfo = await this.postMessage(PlatformMessage.DeviceInformationGet, undefined);
        return 'BrightSign://' + deviceInfo.uniqueIdentifier;
    }

    async getHostPlatform(): Promise<string> {
        const deviceInfo = await this.postMessage(PlatformMessage.DeviceInformationGet, undefined);
        return deviceInfo.platform;
    }

    async getSystemInformation(): ReturnType<SystemInformationInterface['getSystemInformation']> {
        const [deviceInfo, brightsignInfo] = await Promise.all([
            this.postMessage(PlatformMessage.DeviceInformationGet, undefined),
            this.postMessage(PlatformMessage.BsDeviceInfoGet, undefined),
        ]);
        return {
            HardwareInfo: {
                Manufacturer: deviceInfo.platform,
                Model: deviceInfo.model,
            },
            OperatingSystemInfo: {
                Name: deviceInfo.platform,
                Manufacturer: deviceInfo.platform,
                Version: brightsignInfo.version,
            },
        };
    }
}
